// ════════════════════════════════════════════════════════════
// PedidoCompraEditor — crear y editar pedidos a proveedor de cualquier tipo
// ════════════════════════════════════════════════════════════
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, TextField, Select,
  MenuItem, FormControl, InputLabel, Autocomplete, Table, TableBody, TableCell, TableHead,
  TableRow, TableContainer, IconButton, Typography, Box, Alert
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import api, {
  getCategoriasCompra, getProveedoresCompras, getConfigERP, getRecambiosDivision,
  crearPedidoCompra, actualizarPedidoCompra, cambiarEstadoPedidoCompra
} from '../api';

const REGIMENES = [
  { clave: 'nacional',         nombre: 'Nacional (con IVA)' },
  { clave: 'intracomunitaria', nombre: 'Unión Europea (sin IVA)' },
  { clave: 'importacion',      nombre: 'Importación (fuera de la UE)' },
];
const LINEA_VACIA = {
  referencia: '', descripcion: '', cantidad: 1, precio_unitario: 0,
  descuento: 0, tipo_iva: 21, recambio_id: null,
};
const eur = (n: number) => (n || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
const r2 = (n: number) => Math.round(n * 100) / 100;

export default function PedidoCompraEditor({ open, pedido, onClose, onGuardado }: any) {
  const [categorias, setCategorias] = useState<any[]>([]);
  const [proveedores, setProveedores] = useState<any[]>([]);
  const [formas, setFormas] = useState<any[]>([]);
  const [maquinas, setMaquinas] = useState<any[]>([]);
  const [d, setD] = useState<any>({});
  const [lineas, setLineas] = useState<any[]>([]);
  const [busqueda, setBusqueda] = useState('');
  const [resultados, setResultados] = useState<any[]>([]);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!open) return;
    getCategoriasCompra().then(setCategorias).catch(() => {});
    getProveedoresCompras().then(setProveedores).catch(() => {});
    getConfigERP().then((c: any) => setFormas(c.formas_pago || [])).catch(() => {});
    api.get('/maquinas')
      .then((r: any) => setMaquinas(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => setMaquinas([]));
    setBusqueda('');
    setResultados([]);
    if (pedido) {
      setD({
        proveedor_id: pedido.proveedor_id || null,
        tipo_compra: pedido.tipo_compra || 'recambios',
        regimen_compra: pedido.regimen_compra || 'nacional',
        maquina_id: pedido.maquina_id || null,
        fecha_prevista: pedido.fecha_prevista || '',
        forma_pago: pedido.forma_pago || 'contado',
        observaciones: pedido.observaciones || '',
      });
      setLineas((pedido.lineas || []).map((l: any) => ({ ...l })));
    } else {
      setD({
        proveedor_id: null, tipo_compra: 'recambios', regimen_compra: 'nacional',
        maquina_id: null, fecha_prevista: '', forma_pago: 'contado', observaciones: '',
      });
      setLineas([{ ...LINEA_VACIA }]);
    }
  }, [open, pedido]);

  // Búsqueda en el catálogo de recambios
  useEffect(() => {
    if (busqueda.trim().length < 2) { setResultados([]); return; }
    const t = setTimeout(() => {
      getRecambiosDivision(busqueda.trim(), 1, '')
        .then((x: any) => setResultados(x.items || []))
        .catch(() => setResultados([]));
    }, 300);
    return () => clearTimeout(t);
  }, [busqueda]);

  const ivaDe = (regimen: string) => (regimen === 'nacional' ? 21 : 0);

  const elegirProveedor = (p: any) => {
    const regimen = p?.regimen_compra || d.regimen_compra;
    setD({
      ...d, proveedor_id: p ? p.id : null, regimen_compra: regimen,
      tipo_compra: pedido ? d.tipo_compra : (p?.categoria || d.tipo_compra),
    });
    if (p?.regimen_compra) setLineas(lineas.map(l => ({ ...l, tipo_iva: ivaDe(regimen) })));
  };

  const cambiarRegimen = (regimen: string) => {
    setD({ ...d, regimen_compra: regimen });
    setLineas(lineas.map(l => ({ ...l, tipo_iva: ivaDe(regimen) })));
  };

  const añadirCatalogo = (r: any) => {
    if (!r) return;
    const nueva = {
      referencia: r.referencia, descripcion: r.nombre, cantidad: 1,
      precio_unitario: r.precio_compra || 0, descuento: 0,
      tipo_iva: ivaDe(d.regimen_compra), recambio_id: r.id,
    };
    const soloVacia = lineas.length === 1 && !(lineas[0].descripcion || '').trim();
    setLineas(soloVacia ? [nueva] : [...lineas, nueva]);
    setBusqueda('');
    setResultados([]);
  };

  const setLinea = (i: number, campo: string, valor: any) => {
    const nuevas = [...lineas];
    nuevas[i] = { ...nuevas[i], [campo]: valor };
    setLineas(nuevas);
  };

  let base = 0;
  let cuota = 0;
  lineas.forEach(l => {
    const b = (parseFloat(l.cantidad) || 0) * (parseFloat(l.precio_unitario) || 0)
      * (1 - (parseFloat(l.descuento) || 0) / 100);
    base += b;
    cuota += b * (parseFloat(l.tipo_iva) || 0) / 100;
  });
  base = r2(base);
  cuota = r2(cuota);

  const guardar = async (emitir: boolean) => {
    const validas = lineas.filter(l => (l.descripcion || '').trim());
    if (!validas.length) { alert('Añade al menos una línea con descripción'); return; }
    if (emitir && !d.proveedor_id) { alert('Para emitir el pedido hay que elegir el proveedor'); return; }
    const linea_negocio = d.maquina_id ? 'maquinas' : (d.tipo_compra === 'recambios' ? 'recambios' : 'otros');
    const payload = {
      ...d, linea_negocio,
      lineas: validas.map(l => ({
        id: l.id, recambio_id: l.recambio_id || null, referencia: l.referencia || null,
        descripcion: l.descripcion.trim(), cantidad: parseFloat(l.cantidad) || 0,
        precio_unitario: parseFloat(l.precio_unitario) || 0,
        descuento: parseFloat(l.descuento) || 0, tipo_iva: parseFloat(l.tipo_iva) || 0,
        pedido_cliente_linea_id: l.pedido_cliente_linea_id || null,
      })),
    };
    setGuardando(true);
    try {
      let id = pedido?.id;
      let numero = pedido?.numero;
      if (pedido) {
        await actualizarPedidoCompra(pedido.id, payload);
      } else {
        const r = await crearPedidoCompra(payload);
        id = r.id;
        numero = r.numero;
      }
      if (emitir) await cambiarEstadoPedidoCompra(id, 'enviado');
      if (onGuardado) onGuardado({ id, numero, emitido: emitir });
      onClose();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al guardar el pedido');
    } finally {
      setGuardando(false);
    }
  };

  const proveedorSel = proveedores.find((p: any) => p.id === d.proveedor_id) || null;
  const maquinaSel = maquinas.find((m: any) => m.id === d.maquina_id) || null;

  return (
    <Dialog open={open} maxWidth="lg" fullWidth
      onClose={(e, rz) => { if (rz !== 'backdropClick' && rz !== 'escapeKeyDown') onClose(); }}>
      <DialogTitle>{pedido ? `✏️ Pedido ${pedido.numero}` : '🛒 Nuevo pedido a proveedor'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5, mb: 2 }}>
          <Grid item xs={12} md={5}>
            <Autocomplete options={proveedores} value={proveedorSel}
              getOptionLabel={(p: any) => p.nombre || ''}
              onChange={(_, v: any) => elegirProveedor(v)}
              renderOption={(props, p: any) => (
                <li {...props} key={p.id}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.nombre}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {categorias.find((c: any) => c.clave === p.categoria)?.nombre || p.categoria || ''}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(p) => <TextField {...p} size="small" label="Proveedor" />} />
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo de compra</InputLabel>
              <Select value={d.tipo_compra || 'recambios'} label="Tipo de compra"
                onChange={(e) => setD({ ...d, tipo_compra: e.target.value })}>
                {categorias.map((c: any) => <MenuItem key={c.clave} value={c.clave}>{c.nombre}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Régimen fiscal</InputLabel>
              <Select value={d.regimen_compra || 'nacional'} label="Régimen fiscal"
                onChange={(e) => cambiarRegimen(String(e.target.value))}>
                {REGIMENES.map(r => <MenuItem key={r.clave} value={r.clave}>{r.nombre}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={5}>
            <Autocomplete options={maquinas} value={maquinaSel}
              getOptionLabel={(m: any) => `${m.modelo || ''} · ${m.numero_serie || ''}`}
              onChange={(_, v: any) => setD({ ...d, maquina_id: v ? v.id : null })}
              renderInput={(p) => <TextField {...p} size="small" label="Imputar a máquina (opcional)" />} />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField fullWidth size="small" type="date" label="Fecha prevista"
              InputLabelProps={{ shrink: true }} value={d.fecha_prevista || ''}
              onChange={(e) => setD({ ...d, fecha_prevista: e.target.value })} />
          </Grid>
          <Grid item xs={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Forma de pago</InputLabel>
              <Select value={formas.some((f: any) => f.clave === d.forma_pago) ? d.forma_pago : 'contado'}
                label="Forma de pago" onChange={(e) => setD({ ...d, forma_pago: e.target.value })}>
                {formas.map((f: any) => <MenuItem key={f.clave} value={f.clave}>{f.nombre}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Observaciones"
              placeholder="Ej: según oferta nº 1234 del proveedor, plazo de entrega 2 semanas"
              value={d.observaciones || ''} onChange={(e) => setD({ ...d, observaciones: e.target.value })} />
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          <Autocomplete sx={{ flex: 1, minWidth: 260 }}
            options={resultados} inputValue={busqueda} value={null}
            onInputChange={(_, v, reason) => { if (reason !== 'reset') setBusqueda(v); }}
            onChange={(_, v) => añadirCatalogo(v)}
            getOptionLabel={(r: any) => `${r.referencia} — ${r.nombre}`}
            filterOptions={(x) => x}
            noOptionsText={busqueda.length < 2 ? 'Escribe al menos 2 letras' : 'Sin resultados'}
            renderInput={(p) => <TextField {...p} size="small" label="Añadir recambio del catálogo" />} />
          <Button variant="outlined" startIcon={<AddIcon />}
            onClick={() => setLineas([...lineas, { ...LINEA_VACIA, tipo_iva: ivaDe(d.regimen_compra) }])}>
            Línea libre
          </Button>
        </Box>

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 820 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, width: 140 }}>Referencia</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Descripción</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 80 }}>Cant.</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 110 }}>Precio</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 80 }}>Dto %</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 90 }}>IVA</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Importe</TableCell>
                <TableCell sx={{ width: 50 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {lineas.map((l, i) => {
                const importe = (parseFloat(l.cantidad) || 0) * (parseFloat(l.precio_unitario) || 0)
                  * (1 - (parseFloat(l.descuento) || 0) / 100);
                return (
                  <TableRow key={i}>
                    <TableCell>
                      <TextField variant="standard" value={l.referencia || ''}
                        onChange={(e) => setLinea(i, 'referencia', e.target.value)} />
                    </TableCell>
                    <TableCell>
                      <TextField variant="standard" fullWidth placeholder="Descripción del artículo o servicio"
                        value={l.descripcion || ''} onChange={(e) => setLinea(i, 'descripcion', e.target.value)} />
                    </TableCell>
                    <TableCell>
                      <TextField variant="standard" type="number" value={l.cantidad}
                        onChange={(e) => setLinea(i, 'cantidad', e.target.value)} />
                    </TableCell>
                    <TableCell>
                      <TextField variant="standard" type="number" value={l.precio_unitario}
                        onChange={(e) => setLinea(i, 'precio_unitario', e.target.value)} />
                    </TableCell>
                    <TableCell>
                      <TextField variant="standard" type="number" value={l.descuento}
                        onChange={(e) => setLinea(i, 'descuento', e.target.value)} />
                    </TableCell>
                    <TableCell>
                      <Select variant="standard" fullWidth value={Number(l.tipo_iva)}
                        onChange={(e) => setLinea(i, 'tipo_iva', e.target.value)}>
                        {[21, 10, 4, 0].map(p => <MenuItem key={p} value={p}>{p}%</MenuItem>)}
                      </Select>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{eur(importe)}</TableCell>
                    <TableCell>
                      <IconButton size="small" color="error"
                        onClick={() => setLineas(lineas.filter((_, j) => j !== i))}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={7}>
</Grid>
          <Grid item xs={12} md={5}>
            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Base imponible</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{eur(base)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">IVA</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{eur(cuota)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                <Typography sx={{ fontWeight: 700 }}>TOTAL</Typography>
                <Typography sx={{ fontWeight: 800 }}>{eur(base + cuota)}</Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="outlined" onClick={() => guardar(false)} disabled={guardando}>
          Guardar borrador
        </Button>
        <Button variant="contained" onClick={() => guardar(true)} disabled={guardando} sx={{ bgcolor: '#4f46e5' }}>
          {guardando ? 'Guardando...' : 'Guardar y emitir pedido'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
